import { FC, useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { spokenFieldHint } from '@eneco/components/spoken-field/spoken-field';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import useDC from '@eneco/dc/useDC';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { Box, InputUsage, Text, TextLink, RadioGroup, RadioButton, Stack } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { UsageStepRendering } from '@sitecore/types/UsageStep';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { StepShell } from '../../components/StepShell/StepShell';
import { energyTypeIncludes, getMaxUsageValue } from '../../helpers';
import { useFlowTracking, useFlowStep } from '../../hooks';
import { EnergyType } from '../../types';

interface FormFields {
  hasDoubleMeter?: string;
  usageElectricityHigh?: number;
  usageElectricityLow?: number;
  usageGas?: number;
  usageWarmth?: number;
  usageWater?: number;
}

export const UsageStep: FC = () => {
  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();

  const {
    energyType = EnergyType.Electricity,
    hasDoubleMeter = true,
    usageElectricityHigh,
    usageElectricityLow,
    usageGas,
    usageWarmth,
    usageWater,
    discountCode,
  } = useFlowSelector(state => state).context;

  const { UsageStep: { fields: textualData } = {} } = usePlaceholderContent<{ UsageStep: UsageStepRendering }>();

  const { label } = useDC();

  const MAX_USAGE_VALUE_ELECTRICITY_HIGH = getMaxUsageValue('ELECTRICITY_HIGH', label);
  const MAX_USAGE_VALUE_ELECTRICITY_LOW = getMaxUsageValue('ELECTRICITY_LOW', label);
  const MAX_USAGE_VALUE_GAS = getMaxUsageValue('GAS', label);
  const MAX_USAGE_VALUE_WARMTH = getMaxUsageValue('WARMTH', label);
  const MAX_USAGE_VALUE_WATER = getMaxUsageValue('WATER', label);

  const [shouldShowDoubleMeter, setShouldShowDoubleMeter] = useState(!!hasDoubleMeter);
  const [shouldShowInputFields, setShouldShowInputFields] = useState(
    !energyTypeIncludes(energyType, EnergyType.Electricity) || hasDoubleMeter !== undefined,
  );
  const [shouldShowBlockingNotification, setShouldShowBlockingNotification] = useState(false);
  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const { trackFillUsage } = useFlowTracking();

  const {
    form: {
      register,
      unregister,
      handleSubmit,
      control,
      resetField,
      watch,
      formState: { errors, isSubmitSuccessful },
    },
  } = useFlowStep<FormFields>({
    mode: 'onChange',
    buildSchema: () =>
      yup.object({
        ...(energyTypeIncludes(energyType, EnergyType.Electricity) && {
          hasDoubleMeter: yup.string().required(textualData?.meterType?.meterTypeRadio?.value?.requiredMessage),

          ...(shouldShowInputFields && {
            usageElectricityHigh: yup
              .number()
              .integer(textualData?.usage?.electricityHighFormField?.value?.validationMessage)
              .typeError(textualData?.usage?.electricityHighFormField?.value?.validationMessage ?? '')
              .required(textualData?.usage?.electricityHighFormField?.value?.requiredMessage)
              .min(1, textualData?.usage?.electricityHighFormField?.value?.validationMessage)
              .max(
                MAX_USAGE_VALUE_ELECTRICITY_HIGH,
                textualData?.usage?.electricityHighFormField?.value?.validationMessage,
              ),

            ...(shouldShowDoubleMeter && {
              usageElectricityLow: yup
                .number()
                .integer(textualData?.usage?.electricityLowFormField?.value?.validationMessage)
                .typeError(textualData?.usage?.electricityLowFormField?.value?.validationMessage ?? '')
                .required(textualData?.usage?.electricityLowFormField?.value?.requiredMessage)
                .integer(textualData?.usage?.electricityLowFormField?.value?.validationMessage)
                .min(1, textualData?.usage?.electricityLowFormField?.value?.validationMessage)
                .max(
                  MAX_USAGE_VALUE_ELECTRICITY_LOW,
                  textualData?.usage?.electricityLowFormField?.value?.validationMessage,
                ),
            }),
          }),
        }),

        ...(shouldShowInputFields && {
          ...(energyTypeIncludes(energyType, EnergyType.Gas) && {
            usageGas: yup
              .number()
              .integer(textualData?.usage?.gasFormField?.value?.validationMessage)
              .typeError(textualData?.usage?.gasFormField?.value?.validationMessage ?? '')
              .required(textualData?.usage?.gasFormField?.value?.requiredMessage)
              .min(1, textualData?.usage?.gasFormField?.value?.validationMessage)
              .max(MAX_USAGE_VALUE_GAS, textualData?.usage?.gasFormField?.value?.validationMessage),
          }),

          ...(energyTypeIncludes(energyType, EnergyType.Warmth) && {
            usageWarmth: yup
              .number()
              .integer(textualData?.usage?.warmthFormField?.value?.validationMessage)
              .typeError(textualData?.usage?.warmthFormField?.value?.validationMessage ?? '')
              .required(textualData?.usage?.warmthFormField?.value?.requiredMessage)
              .min(1, textualData?.usage?.warmthFormField?.value?.validationMessage)
              .max(MAX_USAGE_VALUE_WARMTH, textualData?.usage?.warmthFormField?.value?.validationMessage),
          }),

          ...(energyTypeIncludes(energyType, EnergyType.TapWater) && {
            usageWater: yup
              .number()
              .integer(textualData?.usage?.warmWaterFormField?.value?.validationMessage)
              .typeError(textualData?.usage?.warmWaterFormField?.value?.validationMessage ?? '')
              .required(textualData?.usage?.warmWaterFormField?.value?.requiredMessage)
              .min(1, textualData?.usage?.warmWaterFormField?.value?.validationMessage)
              .max(MAX_USAGE_VALUE_WATER, textualData?.usage?.warmWaterFormField?.value?.validationMessage),
          }),
        }),
      }),
    defaultValues: () => ({
      ...(energyTypeIncludes(energyType, EnergyType.Electricity) && {
        hasDoubleMeter: hasDoubleMeter ? 'double' : 'single',
        usageElectricityHigh,
        usageElectricityLow,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.Gas) && {
        usageGas,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.Warmth) && {
        usageWarmth,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.TapWater) && {
        usageWater,
      }),
    }),
    onSubmitSuccess: formData => {
      const currentHasDoubleMeter = formData.hasDoubleMeter === 'double';
      const shouldResetSelectedOffer = currentHasDoubleMeter !== hasDoubleMeter;

      const values = {
        hasDoubleMeter: formData.hasDoubleMeter ? formData.hasDoubleMeter === 'double' : undefined,

        // truthy check to filter out 0 'zero' values
        usageElectricityHigh: Number(formData.usageElectricityHigh) || undefined,
        usageElectricityLow: Number(formData.usageElectricityLow) || undefined,
        usageGas: Number(formData.usageGas) || undefined,
        usageWarmth: Number(formData.usageWarmth) || undefined,
        usageWater: Number(formData.usageWater) || undefined,
        discountCode: shouldResetSelectedOffer ? undefined : discountCode,
      };

      trackFillUsage({ ...values, isUsageKnown: true });

      return values;
    },
  });

  const watchHasDoubleMeter = watch('hasDoubleMeter');
  const watchUsageElectricityHigh = watch('usageElectricityHigh');
  const watchUsageElectricityLow = watch('usageElectricityLow');
  const watchUsageGas = watch('usageGas');

  useEffect(() => {
    if (energyTypeIncludes(energyType, EnergyType.Electricity)) {
      resetField('usageElectricityHigh');
      resetField('usageElectricityLow');

      unregister(['usageElectricityHigh', 'usageElectricityLow']);
    }
  }, [shouldShowDoubleMeter]);

  useEffect(() => {
    if (shouldShowInputFields) {
      resetField('usageElectricityHigh');
      resetField('usageElectricityLow');
      resetField('usageGas');
      resetField('usageWarmth');
      resetField('usageWater');
    }
  }, [shouldShowInputFields]);

  useEffect(() => {
    setShouldShowBlockingNotification(
      (watchUsageElectricityHigh ?? 0) > MAX_USAGE_VALUE_ELECTRICITY_HIGH ||
        (watchUsageElectricityLow ?? 0) > MAX_USAGE_VALUE_ELECTRICITY_LOW ||
        (watchUsageGas ?? 0) > MAX_USAGE_VALUE_GAS,
    );
  }, [watchUsageElectricityLow, watchUsageElectricityHigh, watchUsageGas]);

  useEffect(() => {
    setShouldShowDoubleMeter(watchHasDoubleMeter === 'double');
    setShouldShowInputFields(!energyTypeIncludes(energyType, EnergyType.Electricity) || !!watchHasDoubleMeter);

    setShouldShowOfferAvailabilityNotification(
      watchHasDoubleMeter === 'single' &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content,
    );
  }, [watchHasDoubleMeter]);

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      showNextButton={!shouldShowBlockingNotification}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      {energyTypeIncludes(energyType, EnergyType.Electricity) && (
        <>
          <Stack.Item>
            <Controller
              control={control}
              name="hasDoubleMeter"
              render={({ field: { onChange, value, name, ref } }) => (
                <RadioGroup
                  ref={ref}
                  label={textualData?.meterType?.meterTypeRadio?.value?.label ?? ''}
                  name={name}
                  direction="row"
                  value={value}
                  onValueChange={onChange}
                  error={errors.hasDoubleMeter?.message}>
                  {textualData?.meterType?.meterTypeRadio.value?.options?.map(option => (
                    <RadioButton value={option.name} key={option.name}>
                      {option.label}
                    </RadioButton>
                  ))}
                </RadioGroup>
              )}
            />

            <Box paddingTop="2">
              <Text size="BodyS" color="textLowEmphasis">
                <TrackedDialog
                  title={textualData?.meterType?.dialog?.value?.title}
                  trigger={<TextLink>{textualData?.meterType?.dialog?.value?.triggerText}</TextLink>}>
                  <RichText html={textualData?.meterType?.dialog?.value?.content} />
                </TrackedDialog>
              </Text>
            </Box>
          </Stack.Item>

          {shouldShowInputFields && (
            <>
              <InputUsage
                {...register('usageElectricityHigh')}
                error={errors.usageElectricityHigh?.message}
                label={textualData?.usage?.electricityHighFormField?.value?.label ?? ''}
                hint={spokenFieldHint(
                  textualData?.usage?.electricityHighFormField?.value?.hint,
                  textualData?.usage?.electricityHighFormField?.value?.placeholder,
                )}
                isHintVisuallyHidden
                type="energy"
                unit="kWh"
              />

              {shouldShowDoubleMeter && (
                <InputUsage
                  {...register('usageElectricityLow')}
                  error={errors.usageElectricityLow?.message}
                  label={textualData?.usage?.electricityLowFormField?.value?.label ?? ''}
                  hint={spokenFieldHint(
                    textualData?.usage?.electricityLowFormField?.value?.hint,
                    textualData?.usage?.electricityLowFormField?.value?.placeholder,
                  )}
                  isHintVisuallyHidden
                  type="energy"
                  unit="kWh"
                />
              )}
            </>
          )}
        </>
      )}

      {shouldShowInputFields && (
        <>
          {energyTypeIncludes(energyType, EnergyType.Gas) && (
            <InputUsage
              {...register('usageGas')}
              error={errors.usageGas?.message}
              label={textualData?.usage?.gasFormField?.value?.label ?? ''}
              hint={spokenFieldHint(
                textualData?.usage?.gasFormField?.value?.hint,
                textualData?.usage?.gasFormField?.value?.placeholder,
              )}
              isHintVisuallyHidden
              type="gas"
              unit="m3"
            />
          )}

          {energyTypeIncludes(energyType, EnergyType.Warmth) && (
            <InputUsage
              {...register('usageWarmth')}
              error={errors.usageWarmth?.message}
              label={textualData?.usage?.warmthFormField?.value?.label ?? ''}
              hint={spokenFieldHint(
                textualData?.usage?.warmthFormField?.value?.hint,
                textualData?.usage?.warmthFormField?.value?.placeholder,
              )}
              isHintVisuallyHidden
              type="warmth"
              unit="GJ"
            />
          )}

          {energyTypeIncludes(energyType, EnergyType.TapWater) && (
            <InputUsage
              {...register('usageWater')}
              error={errors.usageWater?.message}
              label={textualData?.usage?.warmWaterFormField?.value?.label ?? ''}
              hint={spokenFieldHint(
                textualData?.usage?.warmWaterFormField?.value?.hint,
                textualData?.usage?.warmWaterFormField?.value?.placeholder,
              )}
              isHintVisuallyHidden
              type="water"
              unit="m3"
            />
          )}
        </>
      )}

      {shouldShowOfferAvailabilityNotification && (
        <TrackedNotificationBox
          isAlert={false}
          label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
          title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
          text={<RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />}
          variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
        />
      )}

      {shouldShowBlockingNotification && (
        <TrackedNotificationBox
          label={textualData?.genericError?.errorUsagesExceededNotification?.value?.title}
          title={textualData?.genericError?.errorUsagesExceededNotification?.value?.title ?? ''}
          isAlert
          text={<RichText html={textualData?.genericError?.errorUsagesExceededNotification?.value?.content} />}
          variant={textualData?.genericError?.errorUsagesExceededNotification?.value?.variant}
        />
      )}
    </StepShell>
  );
};
